<script setup lang="ts">
/**
 * One mode as a tall painted tile that turns over to the real chart it is
 * played on, with its ring count and clock in kit chips. The turn is
 * `rotateY` 180 degrees. A tap anywhere turns it; a hovering mouse turns it by
 * hover alone; the corner compass does the same for the keyboard and is the
 * 44 px target. Each face carries its own compass, so it turns over with the
 * card instead of hanging still in front of it; the hidden face's one is out
 * of the tab order. Both faces stay in the reading order.
 */
import { ref } from 'vue';

import { useI18n } from '@/i18n';
import GameIcon from '@/ui/kit/GameIcon.vue';

import type { ModeEntry } from '../landing-content';

defineProps<{ mode: ModeEntry }>();
const { t } = useI18n();
const flipped = ref(false);
</script>

<template>
  <div class="stage" :class="{ flipped }" @click="flipped = !flipped">
    <div class="flip">
      <div class="face front">
        <picture>
          <source :srcset="mode.painting" type="image/webp" />
          <img class="painting" :src="mode.paintingJpg" :alt="t(mode.nameKey)" width="900" height="604" loading="lazy" decoding="async" />
        </picture>
        <button
          type="button"
          class="k-knob turn"
          :tabindex="flipped ? -1 : 0"
          :aria-hidden="flipped ? 'true' : undefined"
          :aria-label="t('landing.modes.showChart')"
          @click.stop="flipped = true"
        >
          <GameIcon name="compass" :size="34" />
        </button>
        <div class="foot">
          <span class="k-header plate"><span class="k-title">{{ t(mode.nameKey) }}</span></span>
          <p class="blurb">{{ t(mode.blurbKey, { cap: mode.cap }) }}</p>
        </div>
      </div>

      <div class="face back k-panel">
        <button
          type="button"
          class="k-knob turn"
          :tabindex="flipped ? 0 : -1"
          :aria-hidden="flipped ? undefined : 'true'"
          :aria-label="t('landing.modes.showPainting')"
          @click.stop="flipped = false"
        >
          <GameIcon name="compass" :size="34" />
        </button>
        <h3 class="k-title back-name">{{ t(mode.nameKey) }}</h3>
        <div class="k-well mode-chart"><img :src="mode.chart" alt="" width="240" height="240" loading="lazy" decoding="async" /></div>
        <ul class="chips">
          <li class="k-chip">
            <GameIcon name="flag" :size="30" />
            <span>{{ mode.rings > 0 ? t('landing.modes.back.rings', { count: mode.rings }) : t('landing.modes.back.noRings') }}</span>
          </li>
          <li class="k-chip">
            <GameIcon name="hourglass" :size="30" />
            <span>{{ t('landing.modes.back.clock', { n: mode.minutes }) }}</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style scoped>
.stage {
  position: relative;
  height: 100%;
  perspective: 1000px;
  cursor: pointer;
  touch-action: manipulation;
}

.flip {
  display: grid;
  height: 100%;
  transform-style: preserve-3d;
  transition: transform 520ms var(--ease);
}

.flipped .flip {
  transform: rotateY(180deg);
}

@media (hover: hover) and (pointer: fine) {
  .stage:hover .flip {
    transform: rotateY(180deg);
  }
}

/* Both faces share one grid cell, so the tile is as tall as the taller face. */
.face {
  grid-area: 1 / 1;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  min-height: 0;
}

.front {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  background: #0d141c;
  box-shadow: 0 0 0 3px #0d141c, 0 0 0 6px #b98a3a, 0 14px 22px rgba(0, 0, 0, 0.5);
}

.painting {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.foot {
  position: absolute;
  inset: auto 0 0 0;
  display: grid;
  gap: 8px;
  padding: 40px 8px 8px;
  background: linear-gradient(0deg, rgba(8, 14, 21, 0.95) 30%, rgba(8, 14, 21, 0));
}

.plate {
  --h: 52px;
  padding-inline: 34px;
}

.plate .k-title {
  font-size: 1.25rem;
  text-transform: uppercase;
  white-space: nowrap;
}

.blurb {
  margin: 0;
  padding: 0 6px 4px;
  font-family: var(--font-game-body);
  font-weight: 600;
  font-size: 0.82rem;
  line-height: 1.3;
  color: var(--kit-cream);
  text-shadow: 0 1px 0 var(--kit-ink);
}

.back {
  position: relative;
  transform: rotateY(180deg);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 22px 16px 16px;
}

.back-name {
  font-size: 1.4rem;
  text-transform: uppercase;
  text-align: center;
}

.mode-chart {
  flex: 1;
  display: grid;
  place-items: center;
  min-height: 0;
  width: 100%;
  padding: 10px;
}

.mode-chart img {
  width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.chips .k-chip {
  font-size: 0.95rem;
}

.turn {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 2;
}
</style>
